// Track 3.1 — Coach event bus.
//
// useVisionPipeline emits state-transition events (baseline locked, position
// changed, new best CdA, calibration dropped, sustained steady-state). The
// useAeroCoachTriggers hook subscribes, debounces, and turns events into
// proactive Aero AI asks so the rider hears something the moment the system
// notices it.
//
// Why a tiny event bus instead of just watching the pipeline state from a
// hook? Two reasons:
//   1. Events carry transition semantics (the *delta*, not just the current
//      value) — we want "position_changed from hoods → drops" not "position
//      is drops now and was drops 200ms ago".
//   2. The bus is the seam where proactive logic lives. The pipeline stays
//      a pure state machine; the trigger hook owns rate-limiting and the
//      decision of *whether* to bother the rider.
//
// Memory-only — never persisted. A fresh session starts with a fresh bus.

export type CoachEventType =
  | 'baseline_locked'
  | 'position_changed'
  | 'steady_state_45s'
  | 'best_cda'
  | 'position_held_60s'
  | 'calibration_dropped';

export interface CoachEvent {
  type: CoachEventType;
  /** monotonic ms timestamp (performance.now()). */
  t: number;
  /** Type-specific payload — small object the trigger hook turns into the
   *  proactive Aero AI prompt + context. */
  data: Record<string, any>;
}

export type CoachListener = (e: CoachEvent) => void;

export class CoachEventBus {
  private listeners: Set<CoachListener> = new Set();

  emit(event: CoachEvent): void {
    this.listeners.forEach((fn) => {
      try {
        fn(event);
      } catch (err) {
        // A failing listener should not stop the others or the pipeline.
        console.warn('CoachEventBus listener threw', err);
      }
    });
  }

  subscribe(fn: CoachListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  clear(): void {
    this.listeners.clear();
  }
}

/** State the pipeline carries between ticks to detect transitions. The
 *  reducer is intentionally tiny — no allocations on the hot path. */
export interface CoachStateMachine {
  baselineWasLocked: boolean;
  lastPositionClass: string | null;
  lastBestCda: number | null;
  steadyStartT: number | null;       // when the rider entered current steady window
  steadyLastCda: number | null;      // CdA at the start of the steady window
  steadyAnnouncedT: number | null;   // last time we emitted steady_state_45s
  positionHoldStartT: number | null; // when current position started
  positionHoldAnnouncedT: number | null;
  calibrationWasUsable: boolean;
}

export function newCoachState(): CoachStateMachine {
  return {
    baselineWasLocked: false,
    lastPositionClass: null,
    lastBestCda: null,
    steadyStartT: null,
    steadyLastCda: null,
    steadyAnnouncedT: null,
    positionHoldStartT: null,
    positionHoldAnnouncedT: null,
    calibrationWasUsable: true,
  };
}

interface ProcessInput {
  now: number;
  bus: CoachEventBus;
  state: CoachStateMachine;
  cda: number | null;
  bestCda: number | null;
  baselineCda: number | null;
  isBaselineLocked: boolean;
  positionClass: string | null;
  calibrationUsable: boolean;
  /** Whole-body drag watts at the moment, for context payload. */
  dragPowerW: number | null;
}

const STEADY_DELTA_PCT = 0.015;       // ±1.5% CdA variation defines steady
const STEADY_WINDOW_MS = 45_000;       // 45s in steady = announce
const STEADY_REANNOUNCE_MS = 90_000;   // don't re-announce same steady run more than once / 90s
const POSITION_HOLD_MS = 60_000;       // 60s in same position = announce
const BEST_IMPROVEMENT_PCT = 0.005;    // 0.5% improvement to trigger best_cda (avoid noise)

/** Process one pipeline tick. Pure aside from emitting on the bus + mutating
 *  the passed-in state object (kept by-ref so the caller doesn't pay an
 *  allocation per tick). */
export function processCoachTick(input: ProcessInput): void {
  const {
    now,
    bus,
    state,
    cda,
    bestCda,
    baselineCda,
    isBaselineLocked,
    positionClass,
    calibrationUsable,
    dragPowerW,
  } = input;

  // baseline_locked — fires once on the rising edge
  if (isBaselineLocked && !state.baselineWasLocked) {
    bus.emit({
      type: 'baseline_locked',
      t: now,
      data: { baseline_cda: baselineCda, position: positionClass },
    });
    state.baselineWasLocked = true;
  }
  if (!isBaselineLocked) state.baselineWasLocked = false;

  // position_changed — every time class flips
  if (positionClass && positionClass !== state.lastPositionClass) {
    if (state.lastPositionClass != null) {
      bus.emit({
        type: 'position_changed',
        t: now,
        data: {
          from: state.lastPositionClass,
          to: positionClass,
          cda,
          drag_power_w: dragPowerW,
        },
      });
    }
    state.lastPositionClass = positionClass;
    state.positionHoldStartT = now;
    state.positionHoldAnnouncedT = null;
    // Position change resets steady-state — different posture, different CdA.
    state.steadyStartT = null;
    state.steadyLastCda = null;
  }

  // best_cda — new best beats prior best by ≥0.5%
  if (
    cda != null &&
    bestCda != null &&
    isBaselineLocked &&
    (state.lastBestCda == null || bestCda < state.lastBestCda * (1 - BEST_IMPROVEMENT_PCT))
  ) {
    bus.emit({
      type: 'best_cda',
      t: now,
      data: {
        best_cda: bestCda,
        previous_best: state.lastBestCda,
        position: positionClass,
        drag_power_w: dragPowerW,
      },
    });
    state.lastBestCda = bestCda;
  } else if (bestCda != null && state.lastBestCda == null) {
    state.lastBestCda = bestCda;
  }

  // steady_state_45s — CdA within ±1.5% for 45s, re-announceable every 90s
  if (cda != null && isBaselineLocked) {
    if (state.steadyStartT == null) {
      state.steadyStartT = now;
      state.steadyLastCda = cda;
    } else if (
      state.steadyLastCda != null &&
      Math.abs(cda - state.steadyLastCda) / state.steadyLastCda <= STEADY_DELTA_PCT
    ) {
      const heldFor = now - state.steadyStartT;
      const sinceLast =
        state.steadyAnnouncedT == null
          ? Infinity
          : now - state.steadyAnnouncedT;
      if (heldFor >= STEADY_WINDOW_MS && sinceLast >= STEADY_REANNOUNCE_MS) {
        bus.emit({
          type: 'steady_state_45s',
          t: now,
          data: {
            cda,
            position: positionClass,
            held_for_s: Math.round(heldFor / 1000),
            drag_power_w: dragPowerW,
          },
        });
        state.steadyAnnouncedT = now;
      }
    } else {
      // CdA drifted out of band; restart the window.
      state.steadyStartT = now;
      state.steadyLastCda = cda;
    }
  }

  // position_held_60s — same class for ≥60s, announce once per hold
  if (
    positionClass &&
    state.positionHoldStartT != null &&
    now - state.positionHoldStartT >= POSITION_HOLD_MS &&
    state.positionHoldAnnouncedT == null
  ) {
    bus.emit({
      type: 'position_held_60s',
      t: now,
      data: {
        position: positionClass,
        held_for_s: Math.round((now - state.positionHoldStartT) / 1000),
        cda,
        drag_power_w: dragPowerW,
      },
    });
    state.positionHoldAnnouncedT = now;
  }

  // calibration_dropped — usable→unusable transition. Skips the reverse
  // (unusable→usable) because re-finding good calibration is invisible to
  // the rider and not worth a beat.
  if (state.calibrationWasUsable && !calibrationUsable) {
    bus.emit({
      type: 'calibration_dropped',
      t: now,
      data: { position: positionClass },
    });
  }
  state.calibrationWasUsable = calibrationUsable;
}
